import {
  PointOfInterest,
  PointOfInterestsDocument,
  PointOfInterestTemplate,
  useUpdatePointOfInterestMutation,
} from '@/__generated__/graphql';
import { DEFAULT_REVIEW_STATUS } from '@/constants';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import { useMemo } from 'react';
import { PillMenu } from '@/components/shared/PillMenu';
import { PillStatus } from '@/components/shared/Pill/types';

const getPillStatus = (status: string): PillStatus => {
  switch (status) {
    case 'Pending review':
      return 'info';
    case 'Accept':
      return 'success';
    case 'False positive':
      return 'failed';
    default:
      return 'info';
  }
};

export const ReviewStatus = ({
  pointOfInterest,
  templateOfSelectedPoi,
}: {
  pointOfInterest: PointOfInterest;
  templateOfSelectedPoi: Partial<PointOfInterestTemplate> | undefined;
}) => {
  const [updateMutation, { loading }] = useUpdatePointOfInterestMutation();
  const { showSnackBar } = useSnackBarMessage({});

  const statusField = useMemo(() => {
    if (!templateOfSelectedPoi?.fields) return null;
    return templateOfSelectedPoi?.fields?.find((field) => field.key === 'internal_review_status');
  }, [templateOfSelectedPoi?.fields]);

  const status = useMemo(() => {
    if (!statusField) return DEFAULT_REVIEW_STATUS;
    // using or to handle case of empty string
    return (
      pointOfInterest.templateData?.find((td) => td.id === statusField.id)?.value ||
      DEFAULT_REVIEW_STATUS
    );
  }, [pointOfInterest.templateData, statusField]);

  if (!statusField) return <></>;

  const handleChange = async (status: string) => {
    const initialTemplateData = templateOfSelectedPoi?.fields?.reduce(
      (accumulator: { [key: string]: string }, templateField) => {
        if (accumulator[templateField.id]) return accumulator;
        const templateDataItem = pointOfInterest.templateData?.find(
          (td) => td.id === templateField.id
        );
        return {
          ...accumulator,
          [templateField.id]: templateDataItem?.value ?? '',
        };
      },
      {} as { [key: string]: string }
    );

    const templateData = Object.entries(initialTemplateData ?? {}).map(([key, value]) => ({
      id: key,
      value: key === statusField.id ? status : value,
    }));
    try {
      await updateMutation({
        variables: {
          input: {
            pointOfInterestId: pointOfInterest.id,
            templateId: templateOfSelectedPoi?.id ?? '',
            templateData,
            lastModifiedFor: 'Update Review Status',
          },
        },
        refetchQueries: [PointOfInterestsDocument],
      });
      showSnackBar('Review status updated successfully', 'success');
    } catch {
      showSnackBar('Failed to update review status. Please contact support', 'error');
    }
  };

  return (
    <PillMenu
      key={`${pointOfInterest.id}`}
      options={statusField.options}
      initialValue={status}
      loading={loading}
      loadingLabel="Updating..."
      onChange={handleChange}
      getPillStatus={getPillStatus}
    />
  );
};
